import { useEffect, useState } from 'react';
import { X, ExternalLink, Edit2, ClipboardList, FileText, Image, Video, Mail, Mic, BookOpen } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import type { Asset, AssetChannelPermission, Channel, Tag, UsageLog } from '../../types/assetRegistry';
import { StatusBadge, AssetTypeBadge, BrandBadge, PermissionBadge } from '../../components/assetRegistry/Badges';

interface AssetDetailProps {
  asset: Asset;
  onClose: () => void;
  onEdit: () => void;
  onLogUsage: () => void;
}

function AssetTypeIcon({ type }: { type: Asset['asset_type'] }) {
  const props = { size: 48, color: '#d1d5db' };
  switch (type) {
    case 'image': return <Image {...props} />;
    case 'video': return <Video {...props} />;
    case 'copy': return <FileText {...props} />;
    case 'email_template': return <Mail {...props} />;
    case 'podcast': return <Mic {...props} />;
    case 'training_module': return <BookOpen {...props} />;
    default: return <FileText {...props} />;
  }
}

const PERM_BG: Record<string, string> = {
  approved: '#f0fdf4', exclusive: '#fffbeb', restricted: '#fef2f2', none: '#f9fafb',
};
const PERM_BORDER: Record<string, string> = {
  approved: '#bbf7d0', exclusive: '#fde68a', restricted: '#fecaca', none: '#e5e7eb',
};

const sectionTitle: React.CSSProperties = {
  fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase',
  letterSpacing: '0.07em', marginBottom: 10,
};

export function AssetDetail({ asset, onClose, onEdit, onLogUsage }: AssetDetailProps) {
  const [permissions, setPermissions] = useState<(AssetChannelPermission & { channels: Channel })[]>([]);
  const [channels, setChannels] = useState<Channel[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [usageLogs, setUsageLogs] = useState<(UsageLog & { channels: Channel })[]>([]);

  useEffect(() => { fetchDetails(); }, [asset.id]); // eslint-disable-line

  async function fetchDetails() {
    const [permRes, channelsRes, tagsRes, logsRes] = await Promise.all([
      supabase.from('asset_channel_permissions').select('*, channels(*)').eq('asset_id', asset.id),
      supabase.from('channels').select('*').order('name'),
      supabase.from('asset_tags').select('tag_id, tags(*)').eq('asset_id', asset.id),
      supabase.from('usage_log').select('*, channels(*)').eq('asset_id', asset.id).order('used_at', { ascending: false }),
    ]);
    if (permRes.data) setPermissions(permRes.data as any);
    if (channelsRes.data) setChannels(channelsRes.data as Channel[]);
    if (tagsRes.data) setTags(tagsRes.data.map((at: any) => at.tags).filter(Boolean));
    if (logsRes.data) setUsageLogs(logsRes.data as any);
  }

  const tagsByCategory = tags.reduce<Record<string, Tag[]>>((acc, tag) => {
    if (!acc[tag.category]) acc[tag.category] = [];
    acc[tag.category].push(tag);
    return acc;
  }, {});

  const getPermForChannel = (channelId: string) => permissions.find(p => p.channel_id === channelId) ?? null;

  return (
    <div className="asset-registry-scope" style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex' }}>
      <div style={{ flex: 1, background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)' }} onClick={onClose} />
      <div style={{ width: 560, background: '#fff', height: '100%', display: 'flex', flexDirection: 'column', boxShadow: '-8px 0 40px rgba(0,0,0,0.15)', overflow: 'hidden' }}>
        <style>{`.ar-panel-header, .ar-panel-header *, .ar-panel-header h2, .ar-panel-header button { color: #ffffff !important; }`}</style>

        {/* Header */}
        <div className="ar-panel-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 24px', background: '#0f2240', flexShrink: 0 }}>
          <h2 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: 16 }}>{asset.title}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.6)', display: 'flex', padding: 4, borderRadius: 6 }}>
            <X size={20} />
          </button>
        </div>

        {/* Scrollable body */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {/* Thumbnail */}
          <div style={{ background: '#f3f4f6', aspectRatio: '16/9', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
            {asset.thumbnail_url
              ? <img src={asset.thumbnail_url} alt={asset.title} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
              : <AssetTypeIcon type={asset.asset_type} />}
          </div>

          <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 20 }}>
            {/* Badges */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              <AssetTypeBadge type={asset.asset_type} />
              <BrandBadge brand={asset.brand_entity} />
              <StatusBadge status={asset.status} />
            </div>

            {/* Description */}
            {asset.description && (
              <p style={{ margin: 0, fontSize: 13, color: '#4b5563', lineHeight: 1.6 }}>{asset.description}</p>
            )}

            {/* Meta */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, background: '#f9fafb', borderRadius: 10, padding: '12px 16px', border: '1px solid #e5e7eb' }}>
              {[
                { label: 'File URL', value: <a href={asset.file_url} target="_blank" rel="noopener noreferrer" style={{ color: '#1B4B8A', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4, wordBreak: 'break-all' }}>{asset.file_url} <ExternalLink size={10} /></a> },
                asset.thumbnail_url ? { label: 'Thumbnail', value: <a href={asset.thumbnail_url} target="_blank" rel="noopener noreferrer" style={{ color: '#1B4B8A', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4, wordBreak: 'break-all' }}>{asset.thumbnail_url} <ExternalLink size={10} /></a> } : null,
                asset.kranum_asset_id ? { label: 'Kranum ID', value: <span style={{ fontSize: 12, fontFamily: 'monospace', color: '#374151' }}>{asset.kranum_asset_id}</span> } : null,
                { label: 'Created', value: <span style={{ fontSize: 12, color: '#374151' }}>{new Date(asset.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</span> },
              ].filter(Boolean).map((row: any) => (
                <div key={row.label} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', width: 90, flexShrink: 0, paddingTop: 1 }}>{row.label}</span>
                  <div style={{ flex: 1 }}>{row.value}</div>
                </div>
              ))}
            </div>

            {/* Channel Permissions */}
            <div>
              <p style={sectionTitle}>Channel Permissions</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {channels.map(channel => {
                  const perm = getPermForChannel(channel.id);
                  const key = perm?.permission_type ?? 'none';
                  return (
                    <div key={channel.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '10px 14px', borderRadius: 8, background: PERM_BG[key], border: `1px solid ${PERM_BORDER[key]}` }}>
                      <div style={{ flex: 1 }}>
                        <p style={{ margin: 0, fontSize: 13, fontWeight: 500, color: '#1f2937' }}>{channel.name.replace(/_/g, ' ')}</p>
                        {perm?.restriction_note && <p style={{ margin: '3px 0 0', fontSize: 12, color: '#6b7280', fontStyle: 'italic' }}>{perm.restriction_note}</p>}
                      </div>
                      <PermissionBadge type={perm?.permission_type ?? null} />
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Tags */}
            {Object.keys(tagsByCategory).length > 0 && (
              <div>
                <p style={sectionTitle}>Tags</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {Object.entries(tagsByCategory).map(([cat, catTags]) => (
                    <div key={cat} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                      <span style={{ fontSize: 12, color: '#6b7280', width: 60, flexShrink: 0, paddingTop: 2, textTransform: 'capitalize' }}>{cat}</span>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {catTags.map(tag => (
                          <span key={tag.id} style={{ padding: '3px 10px', background: '#f3f4f6', color: '#374151', borderRadius: 20, fontSize: 12 }}>{tag.name}</span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Usage History */}
            <div>
              <p style={sectionTitle}>Usage History ({usageLogs.length})</p>
              {usageLogs.length === 0 ? (
                <p style={{ margin: 0, fontSize: 13, color: '#9ca3af', fontStyle: 'italic' }}>No usage logged yet</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {usageLogs.map(log => (
                    <div key={log.id} style={{ background: '#f9fafb', borderRadius: 8, padding: '10px 14px', border: '1px solid #e5e7eb' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                        <span style={{ fontSize: 13, fontWeight: 500, color: '#374151' }}>{log.channels?.name?.replace(/_/g, ' ')}</span>
                        <span style={{ fontSize: 12, color: '#9ca3af', flexShrink: 0 }}>{new Date(log.used_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                      </div>
                      {log.context && <p style={{ margin: '4px 0 0', fontSize: 12, color: '#6b7280' }}>{log.context}</p>}
                      {log.kranum_campaign_id && <p style={{ margin: '3px 0 0', fontSize: 11, color: '#9ca3af', fontFamily: 'monospace' }}>Campaign: {log.kranum_campaign_id}</p>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer actions */}
        <div style={{ display: 'flex', gap: 10, padding: '14px 24px', borderTop: '1px solid #e5e7eb', background: '#f9fafb', flexShrink: 0 }}>
          <button onClick={onEdit} style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '9px 18px', fontSize: 13, fontWeight: 600,
            color: '#1B4B8A', border: '1.5px solid #1B4B8A', borderRadius: 8, background: '#fff', cursor: 'pointer', transition: 'all 0.15s',
          }}
            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = '#1B4B8A'; (e.currentTarget as HTMLButtonElement).style.color = '#fff'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = '#fff'; (e.currentTarget as HTMLButtonElement).style.color = '#1B4B8A'; }}>
            <Edit2 size={14} /> Edit Asset
          </button>
          <button onClick={onLogUsage} style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '9px 18px', fontSize: 13, fontWeight: 600,
            color: '#fff', background: '#1B4B8A', border: '1.5px solid #1B4B8A', borderRadius: 8, cursor: 'pointer', transition: 'background 0.15s',
          }}
            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = '#153d73'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = '#1B4B8A'; }}>
            <ClipboardList size={14} /> Log Usage
          </button>
        </div>
      </div>
    </div>
  );
}
